import { z } from 'zod';
// The invoice numbering module already knows how to shorten a financial year, and two
// implementations of "2026-27 → 26-27" would eventually disagree on a shop's own tags.
import { shortFinancialYear } from '../money/invoice-numbering.js';

/**
 * HOW A SHOP PRESENTS ITSELF — the settings breadth that turns a working product into one a
 * shop is willing to hand a customer.
 *
 * Four small things, and each of them is a thing an owner asked for rather than a thing an
 * engineer wanted to build.
 *
 * **Bank details**, because a bill that says "pay by transfer" and does not say where is a bill
 * that generates a phone call.
 *
 * **Order numbering**, because a shop's order numbers are read out loud, written on tags and
 * matched against a paper book — and until now they borrowed the INVOICE prefix, which meant a
 * shop that set its invoices to "INV" had orders called INV too.
 *
 * **A gallery watermark**, because a boutique that photographs its own work is handing that
 * work to anybody who screenshots it.
 *
 * **Form layouts**, because the fields one shop needs on a customer are not the fields another
 * does, and the alternative to arranging them is a form where half the boxes are always blank.
 *
 * **A bill design**, because the bill is the one piece of paper the customer takes home, and a
 * boutique wants it to look like the boutique rather than like everybody else's.
 */

/* -------------------------------------------------------------------------------------------
 * Bank details
 * ---------------------------------------------------------------------------------------- */

/**
 * What a customer needs in order to pay by transfer.
 *
 * Every field is nullable and the whole section is optional, because most shops in this market
 * take cash and UPI at the counter and would find a required IFSC baffling. `upiId` sits beside
 * the account because for a lot of customers it is the ONLY one of the two they will use.
 *
 * NOT VALIDATED BEYOND SHAPE. An account number's length varies by bank, and refusing a real
 * one because our regex was written against a different bank's format would be worse than
 * printing what the owner typed.
 */
export const shopBankSchema = z
  .object({
    accountName: z.string().trim().max(120).nullable(),
    accountNumber: z.string().trim().max(40).nullable(),
    /** Uppercased on the way in: it is printed, and banks write it in capitals. */
    ifsc: z.string().trim().toUpperCase().max(20).nullable(),
    bankName: z.string().trim().max(120).nullable(),
    branch: z.string().trim().max(120).nullable(),
    upiId: z.string().trim().max(120).nullable(),
    /** Off by default. A shop that has filled this in has not necessarily agreed to print it. */
    showOnInvoice: z.boolean(),
  })
  .partial();
export type ShopBankInput = z.infer<typeof shopBankSchema>;

export interface ShopBankSettings {
  accountName: string | null;
  accountNumber: string | null;
  ifsc: string | null;
  bankName: string | null;
  branch: string | null;
  upiId: string | null;
  showOnInvoice: boolean;
}

export const EMPTY_BANK_SETTINGS: ShopBankSettings = Object.freeze({
  accountName: null,
  accountNumber: null,
  ifsc: null,
  bankName: null,
  branch: null,
  upiId: null,
  showOnInvoice: false,
});

/** True when there is anything worth printing. An empty block under a heading is worse. */
export function hasBankDetails(bank: ShopBankSettings): boolean {
  return Boolean(bank.accountNumber ?? bank.upiId);
}

/* -------------------------------------------------------------------------------------------
 * Order numbering
 * ---------------------------------------------------------------------------------------- */

/**
 * How an ORDER number is built.
 *
 * Its own section rather than a field on the invoicing one, and that separation fixes a real
 * defect: order numbers used to be built from `invoicing.invoicePrefix`, so a shop that named
 * its invoices "INV" got orders called `INV/2026-27/0001` too — the same string on two
 * different documents about the same job, which is exactly the confusion a number exists to
 * prevent.
 *
 * `resetAnnually` is here because shops genuinely disagree. A shop that files by year wants
 * `0001` again every April; a shop with a paper book that runs continuously does not, and
 * having the number restart would put two orders with the same number in one drawer.
 */
export const ORDER_YEAR_STYLES = ['financial_short', 'financial_long', 'none'] as const;
export type OrderYearStyle = (typeof ORDER_YEAR_STYLES)[number];
export const orderYearStyleSchema = z.enum(ORDER_YEAR_STYLES);

export const orderPrefixSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9/-]{0,9}$/, 'Use up to 10 letters, digits, / or -');

export const shopOrderNumberingSchema = z
  .object({
    prefix: orderPrefixSchema,
    yearStyle: orderYearStyleSchema,
    /** How many digits the serial is padded to. Four gives `0001`; a busy shop wants five. */
    padding: z.number().int().min(3).max(8),
  })
  .partial();
export type ShopOrderNumberingInput = z.infer<typeof shopOrderNumberingSchema>;

export interface ShopOrderNumberingSettings {
  prefix: string;
  yearStyle: OrderYearStyle;
  padding: number;
}

export const DEFAULT_ORDER_NUMBERING: ShopOrderNumberingSettings = Object.freeze({
  // Not "INV". An order is not an invoice, and giving them the same prefix is what this
  // section exists to stop.
  prefix: 'ORD',
  // The LONG form, which is what an order number has always looked like in this product. The
  // prefix moving off `INV` is the fix this section exists for; changing the year's shape at
  // the same time would renumber every shop's tags for no reason anybody asked for.
  yearStyle: 'financial_long',
  padding: 4,
});

/**
 * `ORD/26-27/0001`.
 *
 * The year segment is dropped entirely when the style is `none`, rather than left empty — a
 * shop that runs one continuous series wants `ORD/0001`, not `ORD//0001`.
 */
export function formatConfiguredOrderNumber(
  settings: ShopOrderNumberingSettings,
  financialYear: string,
  sequence: number,
): string {
  const serial = String(sequence).padStart(settings.padding, '0');
  if (settings.yearStyle === 'none') return `${settings.prefix}/${serial}`;
  const year =
    settings.yearStyle === 'financial_short' ? shortFinancialYear(financialYear) : financialYear;
  return `${settings.prefix}/${year}/${serial}`;
}

/* -------------------------------------------------------------------------------------------
 * The gallery watermark
 * ---------------------------------------------------------------------------------------- */

export const WATERMARK_POSITIONS = [
  'bottom_right',
  'bottom_left',
  'top_right',
  'top_left',
  'centre',
] as const;
export type WatermarkPosition = (typeof WATERMARK_POSITIONS)[number];
export const watermarkPositionSchema = z.enum(WATERMARK_POSITIONS);

/**
 * A mark burned into the images a shop shows.
 *
 * The text defaults to nothing rather than to the shop's name, on purpose: switching this on
 * should be a decision somebody makes with the wording in front of them, not one that silently
 * stamps a name across a hundred photographs the moment a checkbox is ticked.
 *
 * `opacity` is a whole percentage because it is set with a slider by somebody who is not
 * thinking in decimals.
 */
export const shopWatermarkSchema = z
  .object({
    enabled: z.boolean(),
    text: z.string().trim().max(60).nullable(),
    position: watermarkPositionSchema,
    opacity: z.number().int().min(10).max(100),
  })
  .partial()
  .refine((value) => value.enabled !== true || (value.text?.length ?? 0) > 0, {
    message: 'Say what the watermark should read before switching it on',
    path: ['text'],
  });
export type ShopWatermarkInput = z.infer<typeof shopWatermarkSchema>;

export interface ShopWatermarkSettings {
  enabled: boolean;
  text: string | null;
  position: WatermarkPosition;
  opacity: number;
}

export const DEFAULT_WATERMARK: ShopWatermarkSettings = Object.freeze({
  enabled: false,
  text: null,
  position: 'bottom_right',
  opacity: 60,
});

/* -------------------------------------------------------------------------------------------
 * The bill design
 * ---------------------------------------------------------------------------------------- */

/**
 * The layouts a bill can be printed in. One list, shared by the settings screen, the API and
 * the renderer, so a template the renderer does not know can never be saved.
 */
export const INVOICE_TEMPLATES = ['classic', 'modern', 'minimal', 'boutique', 'compact'] as const;
export type InvoiceTemplate = (typeof INVOICE_TEMPLATES)[number];
export const invoiceTemplateSchema = z.enum(INVOICE_TEMPLATES);

/**
 * How a shop's bills look: a layout and ONE accent colour.
 *
 * Shop-wide rather than per bill, because a customer comparing two bills from the same shop
 * should see the same shop. The colour is any 6-digit hex — a boutique often has an exact
 * brand colour — and is lower-cased on the way in so `#8B1E3F` and `#8b1e3f` are one colour,
 * not two stored spellings of it.
 */
export const shopInvoiceDesignSchema = z.object({
  template: invoiceTemplateSchema,
  accentColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, 'Use a colour like #1f3a5f')
    .toLowerCase(),
});
export type ShopInvoiceDesignInput = z.infer<typeof shopInvoiceDesignSchema>;

export interface ShopInvoiceDesignSettings {
  template: InvoiceTemplate;
  accentColor: string;
}

export const DEFAULT_INVOICE_DESIGN: ShopInvoiceDesignSettings = Object.freeze({
  template: 'classic',
  // The navy preset: the bill every shop already had looks the same until somebody changes it.
  accentColor: '#1f3a5f',
});

export interface InvoiceAccentPreset {
  readonly name: string;
  readonly hex: string;
}

/**
 * The colours offered before anybody types a hex.
 *
 * Every one is dark enough to carry WHITE text at 4.5:1 or better, and they are spread across
 * the luminance range that allows rather than bunched in it — so they stay told apart on a
 * black-and-white counter printer, where hue is gone and only the grey is left.
 */
export const INVOICE_ACCENT_PRESETS: readonly InvoiceAccentPreset[] = Object.freeze([
  { name: 'Navy', hex: '#1f3a5f' },
  { name: 'Maroon', hex: '#8a2232' },
  { name: 'Teal', hex: '#0e6a6c' },
  { name: 'Forest green', hex: '#2f5a2b' },
  { name: 'Plum', hex: '#451f49' },
  { name: 'Charcoal', hex: '#262626' },
  { name: 'Terracotta', hex: '#a8492b' },
  { name: 'Gold-brown', hex: '#8a6a12' },
]);

/* -------------------------------------------------------------------------------------------
 * Form layouts
 * ---------------------------------------------------------------------------------------- */

/**
 * The forms a shop may rearrange.
 *
 * Three, and they are the three where shops genuinely differ: what a boutique records about a
 * customer, what a shop records about a catalogue line, and what it records about the people
 * who work there. The booking form is deliberately NOT here — its fields are the transaction
 * itself, and a shop that hid the price would have a form that cannot take an order.
 */
export const FORM_KEYS = ['customer', 'product', 'staff'] as const;
export type FormKey = (typeof FORM_KEYS)[number];
export const formKeySchema = z.enum(FORM_KEYS);

export interface FormFieldDefinition {
  readonly key: string;
  /** Default English; the UI resolves `forms.<form>.<key>` through i18next. */
  readonly defaultLabel: string;
  readonly defaultVisible: boolean;
  /**
   * Cannot be hidden. A customer with no name and no number is not a shorter record, it is one
   * nobody can find again — and the API would refuse it anyway, so hiding the box would produce
   * a form that cannot be submitted.
   */
  readonly required?: true;
}

export const FORM_FIELDS: Readonly<Record<FormKey, readonly FormFieldDefinition[]>> = Object.freeze(
  {
    customer: [
      // `name` is the FIRST and LAST name pair, asked as two boxes and stored as one field.
      // It stays a single layout entry because a shop that hid the surname would have hidden
      // half of a field the API treats as one — see `splitCustomerName`.
      { key: 'name', defaultLabel: 'Name', defaultVisible: true, required: true },
      { key: 'primaryPhone', defaultLabel: 'Mobile number', defaultVisible: true, required: true },
      { key: 'whatsappPhone', defaultLabel: 'WhatsApp number', defaultVisible: true },
      { key: 'altPhone', defaultLabel: 'Alternative mobile number', defaultVisible: true },
      { key: 'gender', defaultLabel: 'Gender', defaultVisible: true },
      { key: 'profession', defaultLabel: 'Profession', defaultVisible: true },
      { key: 'preferredStyle', defaultLabel: 'Preferred style', defaultVisible: true },
      { key: 'kind', defaultLabel: 'Customer type', defaultVisible: true },
      { key: 'gstin', defaultLabel: 'GSTIN', defaultVisible: true },
      { key: 'dateOfBirth', defaultLabel: 'Birthday', defaultVisible: true },
      { key: 'anniversary', defaultLabel: 'Anniversary', defaultVisible: true },
      { key: 'email', defaultLabel: 'Email', defaultVisible: true },
      { key: 'address', defaultLabel: 'Address', defaultVisible: true },
      // Tags are deliberately absent: they are filed from the customer's own page rather than
      // typed on the form, so offering to hide a box that is not there would be a lie.
      { key: 'source', defaultLabel: 'How they heard of us', defaultVisible: true },
      { key: 'notes', defaultLabel: 'Notes', defaultVisible: true },
    ],
    product: [
      { key: 'name', defaultLabel: 'Name', defaultVisible: true, required: true },
      { key: 'category', defaultLabel: 'Category', defaultVisible: true, required: true },
      { key: 'pricePaise', defaultLabel: 'Price', defaultVisible: true, required: true },
      { key: 'code', defaultLabel: 'Code', defaultVisible: true },
      { key: 'description', defaultLabel: 'Description', defaultVisible: true },
      { key: 'hsnSac', defaultLabel: 'HSN / SAC', defaultVisible: true },
      { key: 'gstRatePercent', defaultLabel: 'GST rate', defaultVisible: true },
    ],
    staff: [
      { key: 'name', defaultLabel: 'Name', defaultVisible: true, required: true },
      { key: 'phone', defaultLabel: 'Mobile number', defaultVisible: true, required: true },
      { key: 'role', defaultLabel: 'Role', defaultVisible: true, required: true },
      { key: 'craftRole', defaultLabel: 'Trade', defaultVisible: true },
      { key: 'specialisations', defaultLabel: 'Stages they handle', defaultVisible: true },
      { key: 'shift', defaultLabel: 'Working hours', defaultVisible: true },
    ],
  },
);

export function formFields(form: FormKey): readonly FormFieldDefinition[] {
  return FORM_FIELDS[form];
}

export function defaultFormFieldKeys(form: FormKey): string[] {
  return formFields(form)
    .filter((field) => field.defaultVisible)
    .map((field) => field.key);
}

export const formLayoutSchema = z.object({
  form: formKeySchema,
  /** The visible fields, IN ORDER. */
  fields: z.array(z.string().max(40)).max(60),
});
export type FormLayoutInput = z.infer<typeof formLayoutSchema>;

export const shopFormLayoutsSchema = z.array(formLayoutSchema).max(FORM_KEYS.length);

export interface ShopFormLayout {
  form: FormKey;
  fields: string[];
}

/**
 * What a form actually shows.
 *
 * THE REGISTRY WINS, exactly as it does for table columns: a stored key no field defines any
 * more is dropped silently, and a REQUIRED field is put back whatever the stored list says.
 * The second rule is the important one — the API refuses a customer with no name, so a form
 * that let somebody hide that box would be a form that cannot be submitted.
 */
export function resolveFormFields(form: FormKey, stored: readonly string[] | null): string[] {
  const definitions = formFields(form);
  const known = new Set(definitions.map((field) => field.key));
  if (!stored) return defaultFormFieldKeys(form);

  const chosen = stored.filter((key) => known.has(key));
  const out = [...chosen];
  for (const field of definitions) {
    if (field.required === true && !out.includes(field.key)) {
      const index = definitions.findIndex((one) => one.key === field.key);
      out.splice(Math.min(index, out.length), 0, field.key);
    }
  }
  return out;
}

/** What a form component asks, once per field, while rendering. */
export function showsField(fields: readonly string[], key: string): boolean {
  return fields.includes(key);
}
